import React, { useState, useMemo } from 'react';
import { localized } from '../i18n/translations';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import NotifyAvailable from "../components/NotifyAvailable";
import CustomImage from '../components/CustomImage';
import SEO from '../components/SEO';
import axios from 'axios';
import { applyCountryPrices } from '../utils/countryPricing';
import { getPriceGroup } from '../utils/track';
import apiUrl from '../components/Url';
import BeicaSaKutijama from '../images/BeicaSaKutijama.png';

// Little stars scattered behind the landing — positions in %, so they scale with the hero
const STARS = [
  [6, 18, 14], [14, 70, 10], [22, 34, 8], [31, 84, 12], [44, 12, 9], [57, 78, 8],
  [66, 22, 13], [74, 62, 9], [83, 14, 11], [91, 46, 8], [88, 82, 14], [4, 52, 9],
];

function Star({ size, className = '', style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} fill="currentColor" aria-hidden="true">
      <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
    </svg>
  );
}

// Shown at /kolekcije/novogodisnja until New Year products are published
function NewYearComingSoon({ translate, categories }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');
    try {
      await axios.post(`${apiUrl}/api/newsletter/subscribe`, { email, source: 'bulka' });
      setStatus('done');
    } catch (_) {
      setStatus('error');
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Novogodišnja kolekcija uskoro - Bulka Ceramics"
        description="Ručno rađeni keramički ukrasi i pokloni za praznike stižu uskoro. Prijavite se i saznajte prvi."
        type="website"
        name="Bulka Ceramics"
      />

      <section className="relative overflow-hidden bg-bgSkin">
        {STARS.map(([left, top, size], i) => (
          <Star
            key={i}
            size={size}
            className={`absolute text-brownSkin/30 ${i % 3 === 0 ? 'animate-pulse' : ''}`}
            style={{ left: `${left}%`, top: `${top}%` }}
          />
        ))}

        <div className="relative max-w-[1100px] mx-auto px-4 py-16 768:py-24 flex flex-col-reverse 830:flex-row items-center gap-10 830:gap-16">
          <div className="flex-1 text-center 830:text-left">
            <h1 className="text-4xl 768:text-5xl font-semibold leading-tight text-gray-900">
              {translate("New Year collection is coming soon")}
            </h1>
            <p className="mt-4 text-lg text-gray-600 max-w-[480px] mx-auto 830:mx-0">
              {translate("Handmade ceramic ornaments and gifts for the holidays, made in small batches.")}
            </p>

            {status === 'done' ? (
              <div className="mt-8 inline-flex items-center gap-3 rounded-xl bg-white px-5 py-4 text-left text-gray-800 shadow-sm">
                <svg className="w-6 h-6 flex-shrink-0 text-brownSkin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {translate("Thank you! We'll let you know as soon as the collection is out.")}
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="mt-8 max-w-[480px] mx-auto 830:mx-0">
                <p className="text-sm text-gray-600 mb-3">{translate("Leave your email and we'll let you know as soon as it's out.")}</p>
                <div className="flex flex-col 500:flex-row gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (status !== 'sending') setStatus('idle'); }}
                    placeholder={translate("Your email")}
                    aria-label={translate("Your email")}
                    autoComplete="email"
                    className="flex-1 rounded-lg border border-darkerSkin bg-white px-4 py-3 text-[15px] text-gray-900 outline-none focus:border-brownSkin focus:ring-2 focus:ring-brownSkin/20"
                  />
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="rounded-lg bg-brownSkin px-6 py-3 font-semibold text-white transition-colors hover:bg-[#8c5a30] disabled:opacity-60"
                  >
                    {status === 'sending' ? translate("Processing...") : translate("Notify me")}
                  </button>
                </div>
                {status === 'invalid' && <p className="mt-2 text-sm text-red-600">{translate("Invalid email address")}</p>}
                {status === 'error' && <p className="mt-2 text-sm text-red-600">{translate("Something went wrong. Please try again.")}</p>}
              </form>
            )}

          </div>

          <div className="relative flex-shrink-0 w-[260px] 768:w-[340px]">
            <div className="absolute inset-4 rounded-full bg-white/60 blur-2xl" aria-hidden="true"></div>
            <img src={BeicaSaKutijama} alt="Bulka s novogodišnjim poklonima" className="relative w-full" />
          </div>
        </div>
      </section>

      <Footer categories={categories} />
    </div>
  );
}

function NewYearProducts({ cartItems, productsData, addToCart, currency, country, apiCountry, translate, categories }) {
  const [showModal, setShowModal] = useState(false);
  const [notifyProductId, setNotifyProductId] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [isLoading, setIsLoading] = useState(true);
  const [newYearProducts, setNewYearProducts] = useState([]);

  // Fetch New Year's products from API
  React.useEffect(() => {
    const fetchNewYearProducts = async () => {
      try {
        setIsLoading(true);
        // Use apiCountry prop for API calls (actual country code: BA, RS, HR, ME)
        const countryCode = apiCountry || "BA";
        console.log('NewYearProducts: Fetching for country:', countryCode);
        const response = await axios.get(`${apiUrl}/api/new-year-products?country=${countryCode}`);
        setNewYearProducts(applyCountryPrices(response.data || [], apiCountry, getPriceGroup()));
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching New Year products:', error);
        setIsLoading(false);
      }
    };

    // Only fetch if apiCountry is set
    if (apiCountry) {
      fetchNewYearProducts();
    }
  }, [apiCountry]); // Refetch when apiCountry changes

  const closeModal = () => {
    setShowModal(false);
  };

  // Sort products based on sortBy
  const sortedProducts = useMemo(() => {
    if (!newYearProducts || newYearProducts.length === 0) return [];
    
    let sorted = [...newYearProducts];
    
    if (sortBy === 'price-low') {
      return sorted.sort((a, b) => {
        const priceA = a.is_on_sale && a.sale_discount_percentage > 0 
          ? a[`discounted_${currency}`] 
          : a[currency];
        const priceB = b.is_on_sale && b.sale_discount_percentage > 0 
          ? b[`discounted_${currency}`] 
          : b[currency];
        return priceA - priceB;
      });
    } else if (sortBy === 'price-high') {
      return sorted.sort((a, b) => {
        const priceA = a.is_on_sale && a.sale_discount_percentage > 0 
          ? a[`discounted_${currency}`] 
          : a[currency];
        const priceB = b.is_on_sale && b.sale_discount_percentage > 0 
          ? b[`discounted_${currency}`] 
          : b[currency];
        return priceB - priceA;
      });
    } else if (sortBy === 'date-new') {
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'date-old') {
      return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    return sorted;
  }, [newYearProducts, sortBy, currency]);

  const productsItems = (!isLoading && sortedProducts) ? sortedProducts.map(product => {
    const category = categories.find(cat => cat.id === product.category);
    
    return (
      <div className="group" key={product.id}>
        <Link to={`/proizvod/${category?.path || 'default'}/${product.path}`} className="block">
          <div className="relative bg-white">
            {/* Sale Badge */}
            {product.is_on_sale && product.sale_discount_percentage > 0 && (
              <div className="absolute top-2 right-2 z-10">
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  -{product.sale_discount_percentage}%
                </span>
              </div>
            )}
            {/* Rasprodato (Sold Out) Badge */}
            {((product.stock_tracking_enabled && product.stock_quantity === 0) || !product.is_in_stock) && (
              <div className={`absolute ${product.is_on_sale && product.sale_discount_percentage > 0 ? 'top-10' : 'top-2'} right-2 z-10`}>
                <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  {translate("Sold out")}
                </span>
              </div>
            )}
            <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
              <CustomImage 
                src={product.imageUrl} 
                alt={product.imageAlt} 
                className="h-full w-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" 
              />
            </div>
            <div className="p-2">
              <div>
                <Link to={`/proizvod/${category?.path || 'default'}/${product.path}`} className="no-underline text-brownSkin inline-block">
                  <h3 className="text-base font-medium leading-tight mb-1 text-brownSkin hover:text-brownSkin/80 transition-colors duration-200">
                    {localized(product, 'name', country)}
                  </h3>
                </Link>
                <div className="product-card__price-badge">
                  <div className="product-price">
                    {product.is_on_sale && product.sale_discount_percentage > 0 ? (
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-brownSkin font-medium">
                            {Number(product[`discounted_${currency}`] || 0).toFixed(2)} {currency}
                          </span>
                          <span className="text-sm text-gray-500 line-through">
                            {Number(product[currency] || 0).toFixed(2)} {currency}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-brownSkin font-medium">
                          {Number(product[currency] || 0).toFixed(2)} {currency}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }) : [];

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  // Nothing published yet → the coming-soon landing instead of an empty grid
  if (!isLoading && sortedProducts.length === 0) {
    return <NewYearComingSoon translate={translate} categories={categories} />;
  }

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Novogodišnja Kolekcija - Bulka Ceramics"
        description="Pregledajte našu posebnu novogodišnju kolekciju ručno rađene keramike"
        type="website"
        name="Bulka Ceramics"
      />

      {showModal && <NotifyAvailable onClose={closeModal} notifyProductId={notifyProductId} />}

      {/* Sorting Controls */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-brownSkin font-medium">
            {isLoading ? (
              <div className="h-6 bg-gray-200 rounded animate-pulse w-32"></div>
            ) : (
              translate("Showing {count} products").replace('{count}', sortedProducts.length)
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="SortBy" className="text-brownSkin font-medium text-sm">{translate("Sort by:")}</label>
            </div>

            {/* Sort Select */}
            <div className="relative">
              <select
                name="sort_by"
                id="SortBy"
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="appearance-none bg-white border border-brownSkin/30 rounded-md px-4 py-2 pr-10 focus:ring-brownSkin focus:border-brownSkin focus:ring-1 text-sm min-w-[200px] text-brownSkin"
              >
                <option value="default">{translate("Featured")}</option>
                <option value="price-low">{translate("Price, low to high")}</option>
                <option value="price-high">{translate("Price, high to low")}</option>
                <option value="date-old">{translate("Date, oldest to newest")}</option>
                <option value="date-new">{translate("Date, newest to oldest")}</option>
              </select>
              <svg className="w-5 h-5 text-brownSkin absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Products Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-brownSkin mb-4">
            {translate("New Year Collection")}
          </h2>
          <div className="w-24 h-1 bg-brownSkin rounded-full mx-auto mb-6"></div>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="group">
                <div className="relative bg-white">
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden">
                    <div className="h-full w-full bg-gray-200 rounded-xl animate-pulse"></div>
                  </div>
                  <div className="p-2">
                    <div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : sortedProducts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
            {productsItems}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎄</div>
            <h3 className="text-2xl font-bold text-brownSkin mb-2">{translate("No products in the New Year collection")}</h3>
            <p className="text-gray-600 mb-8">{translate("No products currently available to display.")}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-darkerSkin text-brownSkin px-8 py-3 rounded-full font-medium hover:bg-brownSkin hover:text-white transition-all duration-300"
            >
              {translate("Back to home")}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
            </Link>
          </div>
        )}
      </div>

      <Footer categories={categories} />
    </div>
  );
}

export default NewYearProducts;

